import { UserProfile } from '@dynamic-labs/sdk-react-core';
import { useMemo } from 'react';

/**
 * Custom hook to determine if a user is a basic user based on their wallet credentials.
 * @function useBasicUser
 * @param {UserProfile | undefined} user - The user profile.
 * @returns {boolean} True if the user is a basic user, false otherwise.
 */
export const useBasicUser = (user: UserProfile | undefined) => {
  return useMemo(() => {
    const verifiedCredentials = user?.verifiedCredentials || [];

    // Get all wallet names
    const walletNames = verifiedCredentials
      .filter((wallet) => wallet.address !== undefined)
      .map((wallet) => wallet.walletName);

    const hasDynamicWallet =
      walletNames.includes('turnkeyhd') || walletNames.includes('dynamicwaas');
    const hasZerodevWallet = walletNames.includes('zerodev');
    const totalWallets = walletNames.length;

    // User has only one wallet and it's the 'turnkeyhd' wallet
    const isSingleDynamicWalletUser = totalWallets === 1 && hasDynamicWallet;

    // User has exactly two wallets, one is 'turnkeyhd' and the other is 'zerodev'
    const isDynamicAndZerodevUser =
      totalWallets === 2 && hasDynamicWallet && hasZerodevWallet;

    // Basic user if either rule is satisfied
    return isSingleDynamicWalletUser || isDynamicAndZerodevUser;
  }, [user?.verifiedCredentials]);
};
